import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Mail, Phone, MapPin, Send, Clock, 
  MessageSquare, Building
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  'TrackOtic',
  'IoT Solutions',
  'Enterprise Software',
  'Cybersecurity',
  'Cloud Infrastructure',
  'Embedded Systems',
  'Other'
];

const contactInfo = [
  {
    icon: Mail,
    label: 'Email',
    value: 'contact@sentinelxtech.com',
    href: 'mailto:contact@sentinelxtech.com'
  },
  {
    icon: Phone,
    label: 'Phone',
    value: '+91 79825 85277',
    href: 'tel:+917982585277'
  },
  {
    icon: MapPin,
    label: 'Office',
    value: 'Villa No 97, Basement 3, Block 3, Charmwood Village, Faridabad',
    href: '#'
  },
  {
    icon: Clock,
    label: 'Hours',
    value: 'Mon-Fri: 9AM - 6PM IST',
    href: '#'
  }
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service_interest: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, service_interest: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.service_interest || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service_interest: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-16 bg-white">
      {/* Hero */}
      <section className="py-20 gradient-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <Badge className="mb-6 bg-blue-100 text-blue-700 border-blue-200">
              GET IN TOUCH
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Let&apos;s Start a
              <span className="text-primary"> Conversation</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Have a project in mind? We&apos;d love to hear about it. 
              Fill out the form below and our team will get back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3 bg-white rounded-2xl p-8 shadow-soft"
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-700">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      className="h-12 border-gray-300 focus:border-primary"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="h-12 border-gray-300 focus:border-primary"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-gray-700">Company</Label>
                    <Input
                      id="company"
                      name="company"
                      placeholder="Your Company"
                      value={formData.company}
                      onChange={handleChange}
                      className="h-12 border-gray-300 focus:border-primary"
                      data-testid="contact-company-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-700">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={handleChange}
                      className="h-12 border-gray-300 focus:border-primary"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2 relative" style={{ zIndex: 100 }}>
                  <Label className="text-gray-700">Service Interest *</Label>
                  <Select value={formData.service_interest} onValueChange={handleSelectChange}>
                    <SelectTrigger 
                      className="h-12 border-gray-300"
                      data-testid="contact-service-select"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999] bg-white">
                      {services.map((service) => (
                        <SelectItem key={service} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-700">Project Details *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your project, requirements, and timeline..."
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="border-gray-300 focus:border-primary resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto bg-primary hover:bg-blue-700 text-white font-semibold h-12 px-8 rounded-lg"
                  data-testid="contact-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2 space-y-6"
            >
              <div className="bg-white rounded-2xl p-6 shadow-soft">
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-6">
                  {contactInfo.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      className="flex items-start gap-4 group"
                    >
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          {item.label}
                        </div>
                        <div className="text-gray-900 group-hover:text-primary transition-colors">
                          {item.value}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-soft">
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">Quick Response</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Our team typically responds within 24 hours during business days. 
                  For urgent inquiries, please call our hotline.
                </p>
                <div className="flex items-center gap-2 text-primary">
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm font-medium">Average response: 4 hours</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                <Building className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">Enterprise Inquiries</h3>
                <p className="text-sm text-gray-600 mb-4">
                  For enterprise solutions and partnerships, reach out to our dedicated team.
                </p>
                <a 
                  href="mailto:enterprise@sentinelx.tech"
                  className="text-primary hover:underline text-sm font-medium"
                >
                  enterprise@sentinelx.tech
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
